export interface GpxSummary {
    from?: Date;
    to?: Date;
    distanceMeters: number;
    averageSpeedMps?: number;
    elevationGainMeters?: number;
}

const EARTH_RADIUS_METERS = 6370594;

function haversineMeters(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(a));
}

export function parseGpxSummary(gpxXml: string): GpxSummary {
    const doc = new DOMParser().parseFromString(gpxXml, 'application/xml');
    if (doc.querySelector('parsererror')) {
        throw new Error('Not a valid GPX file');
    }

    const trackPoints = Array.from(doc.querySelectorAll('trkpt'));
    if (trackPoints.length < 2) {
        throw new Error('GPX file contains no track points');
    }

    let distanceMeters = 0;
    let elevationGainMeters = 0;
    let hasElevation = false;
    let firstTime: Date | undefined;
    let lastTime: Date | undefined;
    let previous: { lat: number; lon: number; elevation?: number } | undefined;

    for (const trackPoint of trackPoints) {
        const lat = Number(trackPoint.getAttribute('lat'));
        const lon = Number(trackPoint.getAttribute('lon'));
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            continue;
        }

        const elevationText =
            trackPoint.querySelector('ele')?.textContent ?? undefined;
        const elevation =
            elevationText !== undefined ? Number(elevationText) : undefined;

        const timeText = trackPoint.querySelector('time')?.textContent;
        if (timeText) {
            const time = new Date(timeText);
            if (!Number.isNaN(time.getTime())) {
                firstTime = firstTime ?? time;
                lastTime = time;
            }
        }

        if (previous) {
            distanceMeters += haversineMeters(
                previous.lat,
                previous.lon,
                lat,
                lon
            );
            if (
                elevation !== undefined &&
                Number.isFinite(elevation) &&
                previous.elevation !== undefined &&
                elevation > previous.elevation
            ) {
                elevationGainMeters += elevation - previous.elevation;
                hasElevation = true;
            }
        }
        previous = {
            lat,
            lon,
            elevation:
                elevation !== undefined && Number.isFinite(elevation)
                    ? elevation
                    : undefined
        };
    }

    const elapsedSeconds =
        firstTime && lastTime
            ? (lastTime.getTime() - firstTime.getTime()) / 1000
            : undefined;

    return {
        from: firstTime,
        to: lastTime,
        distanceMeters: Math.round(distanceMeters),
        averageSpeedMps:
            elapsedSeconds && elapsedSeconds > 0
                ? Math.round((distanceMeters / elapsedSeconds) * 100) / 100
                : undefined,
        elevationGainMeters: hasElevation
            ? Math.round(elevationGainMeters)
            : undefined
    };
}
